import {
  Checkbox,
  FormControlLabel,
  Typography,
  useTheme,
} from '@material-ui/core';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';
import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';
import { TitleWithUIGuidelines } from 'component-library/atoms/TitleWithUIGuidelines';
import { PrimaryButton } from 'components/Button';
import CGULabel from 'components/CGULabel';
import DataPolicyLabel from 'components/DataPolicyLabel';
import QuestionTooltip from 'components/QuestionTooltip';
import { RHFCivilityField, RHFPhoneField, RHFTextField } from 'components/RHF';
import { ICustomTheme } from 'interfaces';
import { SubscribeFormValues } from 'interfaces/subscribe.types';
import { clientIDLabelSelector } from 'reducers/app.reducer';
import { PASSWORD_REGEX, PHONE_REGEX, ROUTES } from '../../constants';
import {
  ButtonContainer,
  CheckboxErrorWrapper,
  CheckboxesContainer,
  CheckboxLabel,
  ErrorCaption,
  FormContainer,
  InputsContainer,
  InputsInline,
  LoginLinkContainer,
  RequiredFieldsHint,
  ServerErrorsContainer,
  TooltipContainer,
} from './SubscribeForm.styles';
import { SubscribeFormUIProps } from './SubscribeFormUI.types';
import AddressSection from './components/AddressSection';
import { ADDRESS_FIELDS } from './fieldConfig.utils';
import { useFieldResolver } from './hooks/useFieldResolver';

const SubscribeFormUI = ({
  title,
  submitLabel,
  context,
  isIdentityReadOnly,
  isCompanyNameRequired,
  isReferralEnabled,
  isSubmitting,
  serverErrors,
  validateEmail,
  validateMatricule,
  validateReferralCode,
  fieldConfigs,
}: SubscribeFormUIProps): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();

  const clientIDLabel =
    useSelector(clientIDLabelSelector) || t('auth.signup.matricule');

  const { control, errors, getValues } = useFormContext<SubscribeFormValues>();

  const {
    cfgByName,
    shouldShow,
    isRequired,
    isRequiredForAddress,
    isDisabled,
    label,
    tooltip,
  } = useFieldResolver(fieldConfigs);

  const hasAddressFields = ADDRESS_FIELDS.some((f) => shouldShow(f, false));

  const renderTooltip = (apiName: string) => {
    const text = tooltip(apiName);
    if (!text) return undefined;
    return (
      <TooltipContainer>
        <QuestionTooltip
          tooltipText={text}
          iconColor={theme.colors.paletteFromBO.main}
        />
      </TooltipContainer>
    );
  };

  return (
    <React.Fragment>
      <TitleWithUIGuidelines title={title} />
      <RequiredFieldsHint>{t('core.required_fields_hint')}</RequiredFieldsHint>

      <FormContainer>
        {/* --- Civilité --- */}
        {shouldShow('gender') && (
          <RHFCivilityField
            name="gender"
            control={control}
            label={label('gender', 'auth.signup.civility.label')}
            required={isRequired('gender')}
            disabled={isIdentityReadOnly || isDisabled('gender')}
            error={errors.gender}
            tooltip={renderTooltip('gender')}
          />
        )}

        <InputsContainer>
          {/* --- Nom / Prénom --- */}
          <InputsInline>
            {shouldShow('lastName') && (
              <RHFTextField
                name="lastName"
                control={control}
                label={label('lastName', 'auth.signup.lastname')}
                placeholder={t('auth.signup.lastname_placeholder')}
                required={isRequired('lastName')}
                disabled={isIdentityReadOnly || isDisabled('lastName')}
                error={errors.lastName}
                rules={{
                  ...(isRequired('lastName') && !isDisabled('lastName')
                    ? { required: t('core.required') as string }
                    : {}),
                  ...(!isDisabled('lastName')
                    ? {
                        minLength: {
                          value: 2,
                          message: t('core.min', { min: 2 }),
                        },
                      }
                    : {}),
                }}
                addons={renderTooltip('lastName')}
              />
            )}
            {shouldShow('firstName') && (
              <RHFTextField
                name="firstName"
                control={control}
                label={label('firstName', 'auth.signup.firstname')}
                placeholder={t('auth.signup.firstname_placeholder')}
                required={isRequired('firstName')}
                disabled={isIdentityReadOnly || isDisabled('firstName')}
                error={errors.firstName}
                rules={{
                  ...(isRequired('firstName') && !isDisabled('firstName')
                    ? { required: t('core.required') as string }
                    : {}),
                  ...(!isDisabled('firstName')
                    ? {
                        minLength: {
                          value: 2,
                          message: t('core.min', { min: 2 }),
                        },
                      }
                    : {}),
                }}
                addons={renderTooltip('firstName')}
              />
            )}
          </InputsInline>

          {/* --- Nom d'entreprise --- */}
          {shouldShow('companyName', isCompanyNameRequired) && (
            <RHFTextField
              name="companyName"
              control={control}
              label={label('companyName', 'auth.signup.companyName')}
              placeholder={t('auth.signup.companyPlaceholder')}
              required={isRequired('companyName', isCompanyNameRequired)}
              disabled={isDisabled('companyName')}
              error={errors.companyName}
              rules={
                isRequired('companyName', isCompanyNameRequired) &&
                !isDisabled('companyName')
                  ? { required: t('core.required') as string }
                  : {}
              }
              addons={renderTooltip('companyName')}
            />
          )}

          {/* --- Email / Matricule --- */}
          <InputsInline>
            {shouldShow('email') && (
              <RHFTextField
                name="email"
                control={control}
                label={label('email', 'auth.global.email')}
                placeholder={t('auth.global.email_placeholder')}
                type="email"
                required={isRequired('email')}
                disabled={isIdentityReadOnly || isDisabled('email')}
                error={errors.email}
                rules={{
                  ...(isRequired('email') && !isDisabled('email')
                    ? { required: t('core.required') as string }
                    : {}),
                  ...(!isDisabled('email')
                    ? {
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: t('core.invalid_email_address') as string,
                        },
                      }
                    : {}),
                }}
                addons={renderTooltip('email')}
                asyncValidate={
                  !isIdentityReadOnly && !isDisabled('email')
                    ? validateEmail
                    : undefined
                }
              />
            )}
            {shouldShow('clientId') && (
              <RHFTextField
                name="clientId"
                control={control}
                label={
                  cfgByName['clientId'] ? label('clientId') : clientIDLabel
                }
                placeholder={t('auth.signup.matricule_placeholder')}
                required={isRequired('clientId')}
                disabled={isIdentityReadOnly || isDisabled('clientId')}
                error={errors.clientId}
                rules={
                  isRequired('clientId') && !isDisabled('clientId')
                    ? { required: t('core.required') as string }
                    : {}
                }
                addons={renderTooltip('clientId')}
                asyncValidate={
                  !isIdentityReadOnly && !isDisabled('clientId')
                    ? validateMatricule
                    : undefined
                }
              />
            )}
          </InputsInline>

          {/* --- Téléphone --- */}
          {shouldShow('phone', false) && (
            <RHFPhoneField
              name="phone"
              control={control}
              label={label('phone', 'auth.signup.phone')}
              placeholder={t('auth.signup.phone_placeholder')}
              required={isRequired('phone', false)}
              disabled={isDisabled('phone')}
              error={errors.phone}
              rules={{
                ...(isRequired('phone', false) && !isDisabled('phone')
                  ? { required: t('core.required') as string }
                  : {}),
                ...(!isDisabled('phone')
                  ? {
                      pattern: {
                        value: PHONE_REGEX,
                        message: t('core.invalid_phone_number') as string,
                      },
                    }
                  : {}),
              }}
              addons={renderTooltip('phone')}
            />
          )}

          {/* --- Champs adresse (dynamiques) --- */}
          {hasAddressFields && (
            <AddressSection
              shouldShow={shouldShow}
              isRequired={isRequiredForAddress}
              isDisabled={isDisabled}
              label={label}
              renderTooltip={renderTooltip}
            />
          )}

          {/* --- Mot de passe --- */}
          {shouldShow('password') && context !== 'first-login' && (
            <InputsInline>
              <RHFTextField
                name="password"
                control={control}
                label={label('password', 'auth.global.password')}
                placeholder={t('auth.global.password_placeholder')}
                type="password"
                required={isRequired('password')}
                disabled={isDisabled('password')}
                error={errors.password}
                rules={{
                  ...(isRequired('password') && !isDisabled('password')
                    ? { required: t('core.required') as string }
                    : {}),
                  ...(!isDisabled('password')
                    ? {
                        pattern: {
                          value: PASSWORD_REGEX,
                          message: t('core.password.characters', {
                            min: 14,
                          }) as string,
                        },
                      }
                    : {}),
                }}
                addons={renderTooltip('password')}
              />
              <RHFTextField
                name="confirmPassword"
                control={control}
                label={t('auth.global.confirm_password')}
                placeholder={t('auth.global.confirm_password_placeholder')}
                type="password"
                required={isRequired('password')}
                disabled={isDisabled('password')}
                error={errors.confirmPassword}
                rules={{
                  ...(isRequired('password') && !isDisabled('password')
                    ? { required: t('core.required') as string }
                    : {}),
                  ...(!isDisabled('password')
                    ? {
                        validate: {
                          matchPassword: (value: string) =>
                            value === getValues('password') ||
                            (t('core.password.mismatch') as string),
                        },
                      }
                    : {}),
                }}
              />
            </InputsInline>
          )}

          {/* --- Code parrainage --- */}
          {isReferralEnabled && (
            <RHFTextField
              name="referralCode"
              control={control}
              label={t('auth.signup.referral.label')}
              placeholder={t('auth.signup.referral.placeholder')}
              error={errors.referralCode}
              addons={
                <TooltipContainer>
                  <QuestionTooltip
                    tooltipText={t('auth.signup.referral.tooltip')}
                    iconColor={theme.colors.paletteFromBO.main}
                  />
                </TooltipContainer>
              }
              asyncValidate={validateReferralCode}
            />
          )}
        </InputsContainer>

        {/* --- Checkboxes CGU / Optin / Data policy --- */}
        <CheckboxesContainer>
          <CheckboxLabel>
            <FormControlLabel
              control={
                <Controller
                  name="optin"
                  control={control}
                  render={({ onChange, value }) => (
                    <Checkbox
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
              }
              label={<LabelWithUIGuidelines label={t('auth.signup.contact')} />}
            />
          </CheckboxLabel>

          <CheckboxLabel>
            <FormControlLabel
              control={
                <Controller
                  name="terms"
                  control={control}
                  rules={{
                    validate: (value: boolean) =>
                      value === true ||
                      (t(
                        'auth.signup.required_terms_and_conditions',
                      ) as string),
                  }}
                  render={({ onChange, value }) => (
                    <Checkbox
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
              }
              label={<CGULabel />}
            />
          </CheckboxLabel>
          {errors.terms && (
            <CheckboxErrorWrapper>
              <ErrorCaption>
                <Typography variant="caption" color="error">
                  {errors.terms.message}
                </Typography>
              </ErrorCaption>
            </CheckboxErrorWrapper>
          )}

          <CheckboxLabel>
            <FormControlLabel
              control={
                <Controller
                  name="dataPolicy"
                  control={control}
                  rules={{
                    validate: (value: boolean) =>
                      value === true ||
                      (t('auth.signup.required_data_policy') as string),
                  }}
                  render={({ onChange, value }) => (
                    <Checkbox
                      checked={!!value}
                      onChange={(e) => onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
              }
              label={<DataPolicyLabel />}
            />
          </CheckboxLabel>
          {errors.dataPolicy && (
            <CheckboxErrorWrapper>
              <ErrorCaption>
                <Typography variant="caption" color="error">
                  {errors.dataPolicy.message}
                </Typography>
              </ErrorCaption>
            </CheckboxErrorWrapper>
          )}
        </CheckboxesContainer>

        {/* --- Erreurs serveur --- */}
        {serverErrors && (
          <ServerErrorsContainer>
            {Object.values(serverErrors).map((msg, idx) => (
              <ErrorCaption key={idx}>{msg}</ErrorCaption>
            ))}
          </ServerErrorsContainer>
        )}

        {/* --- CTA --- */}
        <ButtonContainer>
          <PrimaryButton
            label={submitLabel}
            isDisabled={isSubmitting}
            isLoading={false}
          />
        </ButtonContainer>

        {/* --- Lien vers connexion --- */}
        <LoginLinkContainer>
          <BasicTextWithUIGuidelines
            text={t('auth.global.iAlreadyHaveAnAccount')}
          />{' '}
          <LinkWithUIGuidelines
            color={theme.colors.paletteFromBO.main}
            fontWeight="bold"
            url={ROUTES.LOGIN}
            textLink={t('auth.global.login')}
            isInternalLink={true}
            withUnderline
          />
        </LoginLinkContainer>
      </FormContainer>
    </React.Fragment>
  );
};

export default SubscribeFormUI;
